import React, { useState, useEffect } from 'react';
import {
  DecisionGraph,
  JdmConfigProvider,
  NodeKind,
  GraphNode
} from '@gorules/jdm-editor';
import type { NodeSpecification, MinimalNodeProps, MinimalNodeSpecification } from '@gorules/jdm-editor';
import axios from 'axios';
import Login from './Login';
import UserManagement from './components/UserManagement';
import DomainManagement from './components/DomainManagement';
import VersionHistory from './components/VersionHistory';
import ApiKeys from './components/ApiKeys';
import Dashboard from './components/Dashboard';
import './App.css';
import '@gorules/jdm-editor/dist/style.css';

// Custom Decision node renderer component
const DecisionNodeRenderer: React.FC<MinimalNodeProps & { specification: MinimalNodeSpecification }> = (props) => {
  return (
    <GraphNode
      id={props.id}
      specification={props.specification}
      handleLeft={true}
      handleRight={true}
    >
      <div style={{ padding: '8px', fontSize: '12px', color: '#666' }}>
        Key: {(props.data as any)?.content?.key || '(not set)'}
      </div>
    </GraphNode>
  );
};

// Custom Decision node specification for nested rules
const decisionNodeSpec: NodeSpecification = {
  type: NodeKind.Decision,
  displayName: 'Decision',
  shortDescription: 'Call another rule/decision',
  color: 'var(--node-color-purple)',
  icon: <span style={{ fontSize: '14px' }}>📋</span>,
  generateNode: () => ({
    name: 'Decision',
    content: {
      key: '',
      passThrough: false,
      inputField: null,
      outputPath: null,
      executionMode: 'single'
    }
  }),
  renderNode: DecisionNodeRenderer
};

// Custom components to add (only Decision node - defaults are included automatically)
const customComponents = [decisionNodeSpec];

interface Domain {
  id: string;
  name: string;
  description?: string;
  color: string;
}

interface Rule {
  id: string;
  filename: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  size: number;
  domains?: Domain[];
}

interface JDMContent {
  nodes: any[];
  edges: any[];
  name?: string;
  description?: string;
  sampleData?: string; // JSON string of sample request data for simulator
}

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface User {
  id: string;
  username: string;
  name: string;
  role: string;
  permissions: string[];
}

interface SimulationResponse {
  success: boolean;
  result?: any;
  inputData?: any;
  components?: any[];
  ruleId?: string;
  simulatedAt?: string;
  error?: string;
  details?: string;
  trace?: any; // Per-node execution trace with inputs/outputs
  performance?: any; // Performance metrics for each node
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [selectedRule, setSelectedRule] = useState<string | null>(null);
  const [currentRule, setCurrentRule] = useState<JDMContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'list' | 'editor' | 'constants' | 'users' | 'domains' | 'apikeys' | 'dashboard'>('list');
  const [simData, setSimData] = useState('{"creditScore": 750, "annualIncome": 72000, "monthlyDebt": 1800, "monthlyIncome": 6000, "employmentYears": 4, "employmentStatus": "full_time"}');
  const [ruleSimResult, setRuleSimResult] = useState<SimulationResponse | null>(null);
  const [activeSimTab, setActiveSimTab] = useState<'output' | 'input' | 'trace'>('output');
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const [constants, setConstants] = useState<any>(null);
  const [constantsText, setConstantsText] = useState<string>('');
  const [allDomains, setAllDomains] = useState<Domain[]>([]);
  const [showRuleDomainModal, setShowRuleDomainModal] = useState(false);
  const [ruleDomainTarget, setRuleDomainTarget] = useState<Rule | null>(null);
  const [selectedDomainIds, setSelectedDomainIds] = useState<string[]>([]);
  const [versionHistoryRule, setVersionHistoryRule] = useState<Rule | null>(null);

  useEffect(() => {
    // Check for saved token on app load
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('auth_user');
    
    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(parsedUser);
        setupAxiosInterceptors(savedToken);
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      }
    }
  }, []);

  useEffect(() => {
    if (user && token) {
      loadRules();
      loadAllDomains();
    }
  }, [user, token]);

  // Setup axios interceptors for authentication
  const setupAxiosInterceptors = (authToken: string) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    
    // Add response interceptor to handle token expiration
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleLogout();
        }
        return Promise.reject(error);
      }
    );
  };

  const handleLogin = (userData: User, authToken: string) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('auth_token', authToken);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    setupAxiosInterceptors(authToken);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    delete axios.defaults.headers.common['Authorization'];
    setActiveTab('list');
    setRules([]);
    setCurrentRule(null);
    setSelectedRule(null);
    setRuleSimResult(null);
  };

  const hasPermission = (permission: string): boolean => {
    return user?.permissions?.includes(permission) || false;
  };

  const isSuperAdmin = (): boolean => {
    return user?.role === 'super_admin';
  };

  const loadRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/rules`);
      setRules((response.data as any).rules);
    } catch (error) {
      console.error('Error loading rules:', error);
      alert('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const loadAllDomains = async () => {
    try {
      const response = await axios.get(`${API_BASE}/domains`);
      setAllDomains((response.data as any).domains || []);
    } catch (error) {
      console.error('Error loading domains:', error);
    }
  };

  const openRuleDomainModal = (rule: Rule) => {
    setRuleDomainTarget(rule);
    setSelectedDomainIds(rule.domains?.map(d => d.id) || []);
    setShowRuleDomainModal(true);
  };

  const saveRuleDomains = async () => {
    if (!ruleDomainTarget) return;

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/rules/${ruleDomainTarget.id}/domains`, {
        domainIds: selectedDomainIds
      });
      setShowRuleDomainModal(false);
      setRuleDomainTarget(null);
      await loadRules();
    } catch (error: any) {
      console.error('Error saving rule domains:', error);
      alert(error.response?.data?.error || 'Failed to save rule domains');
    } finally {
      setLoading(false);
    }
  };

  const toggleDomainSelection = (domainId: string) => {
    setSelectedDomainIds(prev =>
      prev.includes(domainId)
        ? prev.filter(id => id !== domainId)
        : [...prev, domainId]
    );
  };

  const loadRule = async (ruleId: string) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/rules/${ruleId}`);
      const loadedRule = (response.data as any).rule;
      setCurrentRule(loadedRule);
      setSelectedRule(ruleId);
      setActiveTab('editor');
      // Load rule-specific sample data if available
      if (loadedRule.sampleData) {
        setSimData(loadedRule.sampleData);
      } else {
        // Set default sample data if rule doesn't have any
        setSimData('{}');
      }
      // Clear previous simulation results when loading a new rule
      setRuleSimResult(null);
    } catch (error) {
      console.error('Error loading rule:', error);
      alert('Failed to load rule');
    } finally {
      setLoading(false);
    }
  };

  const saveRule = async () => {
    if (!currentRule) return;

    try {
      setLoading(true);
      // Save the current simulator data with the rule
      const ruleToSave = {
        ...currentRule,
        sampleData: simData
      };
      await axios.post(`${API_BASE}/rules`, {
        rule: ruleToSave,
        filename: selectedRule
      });
      alert('Rule saved successfully');
      await loadRules();
    } catch (error) {
      console.error('Error saving rule:', error);
      alert('Failed to save rule');
    } finally {
      setLoading(false);
    }
  };

  const deleteRule = async (ruleId: string) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      setLoading(true);
      await axios.delete(`${API_BASE}/rules/${ruleId}`);
      await loadRules();
      if (selectedRule === ruleId) {
        setSelectedRule(null);
        setCurrentRule(null);
        setActiveTab('list');
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert('Failed to delete rule');
    } finally {
      setLoading(false);
    }
  };


  const simulateRule = async () => {
    if (!selectedRule) return;
    
    try {
      setLoading(true);
      const data = JSON.parse(simData);
      const response = await axios.post<SimulationResponse>(`${API_BASE}/simulate-rule/${selectedRule}`, { data });
      
      // Check if the server returned an error in the response body
      const simResult = response.data as SimulationResponse;
      if (simResult && simResult.success === false) {
        console.error('Rule simulation failed:', simResult);
        alert(`Simulation failed: ${simResult.error || 'Unknown error'}`);
        // Still set the result to show the error in the UI
        setRuleSimResult(simResult);
        return;
      }
      
      setRuleSimResult(simResult);
    } catch (error) {
      console.error('Error simulating rule:', error);
      if (error instanceof SyntaxError) {
        alert('Invalid JSON in simulator data');
      } else {
        alert('Failed to simulate rule: Network or server error');
      }
      // Clear results on error
      setRuleSimResult(null);
    } finally {
      setLoading(false);
    }
  };

  const createNewRule = () => {
    const defaultSampleData = '{}';
    const newRule: JDMContent = {
      nodes: [],
      edges: [],
      name: 'New Rule',
      description: 'A new business rule',
      sampleData: defaultSampleData
    };
    setCurrentRule(newRule);
    setSelectedRule(null);
    setActiveTab('editor');
    // Set empty sample data for new rule
    setSimData(defaultSampleData);
    // Clear previous simulation results when creating a new rule
    setRuleSimResult(null);
  };

  const loadConstants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/constants`);
      const data = (response.data as any).constants;
      setConstants(data);
      setConstantsText(JSON.stringify(data, null, 2));
    } catch (error) {
      console.error('Error loading constants:', error);
      alert('Failed to load constants');
    } finally {
      setLoading(false);
    }
  };

  const saveConstants = async () => {
    try {
      // Validate JSON before saving
      const parsed = JSON.parse(constantsText);
      setLoading(true);
      await axios.post(`${API_BASE}/constants`, { constants: parsed });
      setConstants(parsed);
      alert('Constants saved successfully');
    } catch (error) {
      if (error instanceof SyntaxError) {
        alert('Invalid JSON format. Please fix the syntax errors.');
      } else {
        console.error('Error saving constants:', error);
        alert('Failed to save constants');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show login screen if not authenticated
  if (!user || !token) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <JdmConfigProvider>
      <div className="app">
        <header className="app-header">
          <div className="header-left">
            <h1>Business Rules Engine</h1>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span className="user-name">{user.name}</span>
              <span className="user-role">({user.role})</span>
              <button className="logout-button" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
          <nav className="nav-tabs">
            <button
              className={activeTab === 'list' ? 'active' : ''}
              onClick={() => setActiveTab('list')}
            >
              Rules List
            </button>
            <button
              className={activeTab === 'editor' ? 'active' : ''}
              onClick={() => setActiveTab('editor')}
              disabled={!currentRule}
            >
              Rule Editor
            </button>
            {hasPermission('write') && (
              <button
                className={activeTab === 'constants' ? 'active' : ''}
                onClick={() => { setActiveTab('constants'); loadConstants(); }}
              >
                Constants
              </button>
            )}
            {isSuperAdmin() && (
              <>
                <button
                  className={activeTab === 'users' ? 'active' : ''}
                  onClick={() => setActiveTab('users')}
                >
                  Users
                </button>
                <button
                  className={activeTab === 'domains' ? 'active' : ''}
                  onClick={() => setActiveTab('domains')}
                >
                  Domains
                </button>
              </>
            )}
            <button
              className={activeTab === 'apikeys' ? 'active' : ''}
              onClick={() => setActiveTab('apikeys')}
            >
              API Keys
            </button>
            <button
              className={activeTab === 'dashboard' ? 'active' : ''}
              onClick={() => setActiveTab('dashboard')}
            >
              Dashboard
            </button>
          </nav>
        </header>

        <main className="main-content">
          {activeTab === 'list' && (
            <div className="rules-list">
              <div className="list-header">
                <h2>Business Rules</h2>
                {hasPermission('write') && (
                  <button className="btn-primary" onClick={createNewRule}>
                    Create New Rule
                  </button>
                )}
              </div>
              
              {loading ? (
                <div className="loading">Loading...</div>
              ) : (
                <div className="rules-grid">
                  {rules.map((rule) => (
                    <div key={rule.id} className="rule-card">
                      <h3>{rule.name}</h3>
                      <p>{rule.description || 'No description'}</p>
                      <div className="rule-domains">
                        {rule.domains && rule.domains.length > 0 ? (
                          rule.domains.map(domain => (
                            <span
                              key={domain.id}
                              className="rule-domain-badge"
                              style={{ backgroundColor: domain.color }}
                              title={domain.description}
                            >
                              {domain.name}
                            </span>
                          ))
                        ) : (
                          <span className="no-domain">No domain</span>
                        )}
                        {hasPermission('write') && (
                          <button
                            className="domain-edit-btn"
                            onClick={(e) => { e.stopPropagation(); openRuleDomainModal(rule); }}
                            title="Edit domains"
                          >
                            ✎
                          </button>
                        )}
                      </div>
                      <div className="rule-meta">
                        <small>Updated: {new Date(rule.updatedAt).toLocaleDateString()}</small>
                        <small>Size: {(rule.size / 1024).toFixed(1)}KB</small>
                      </div>
                      <div className="rule-actions">
                        {hasPermission('read') && (
                          <button
                            className="btn-secondary"
                            onClick={() => loadRule(rule.id)}
                          >
                            {hasPermission('write') ? 'Edit' : 'View'}
                          </button>
                        )}
                        <button
                          className="btn-version-history"
                          onClick={(e) => { e.stopPropagation(); setVersionHistoryRule(rule); }}
                          title="View version history"
                        >
                          History
                        </button>
                        {hasPermission('delete') && (
                          <button
                            className="btn-danger"
                            onClick={() => deleteRule(rule.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'editor' && currentRule && (
            <div className="rule-editor">
              <div className="editor-header">
                <div className="rule-metadata">
                  <div className="rule-name-group">
                    <label htmlFor="rule-name">Rule Name:</label>
                    <input
                      id="rule-name"
                      type="text"
                      className="rule-name-input"
                      value={currentRule.name || ''}
                      onChange={(e) => setCurrentRule({ ...currentRule, name: e.target.value })}
                      placeholder="Enter rule name"
                      disabled={!hasPermission('write')}
                    />
                  </div>
                  <div className="rule-description-group">
                    <label htmlFor="rule-description">Description:</label>
                    <input
                      id="rule-description"
                      type="text"
                      className="rule-description-input"
                      value={currentRule.description || ''}
                      onChange={(e) => setCurrentRule({ ...currentRule, description: e.target.value })}
                      placeholder="Enter rule description (optional)"
                      disabled={!hasPermission('write')}
                    />
                  </div>
                </div>
                <div className="editor-actions">
                  <button
                    className="btn-secondary simulator-toggle"
                    onClick={() => setSimulatorOpen(!simulatorOpen)}
                  >
                    {simulatorOpen ? '🟢 Close Simulator' : '⚪ Open Simulator'}
                  </button>
                  {hasPermission('write') && (
                    <button className="btn-primary" onClick={saveRule} disabled={loading}>
                      {loading ? 'Saving...' : 'Save Rule'}
                    </button>
                  )}
                  {!hasPermission('write') && (
                    <div className="read-only-notice">
                      <em>Read-only mode - you don't have permission to save changes</em>
                    </div>
                  )}
                </div>
              </div>
              
              <div className={`editor-layout ${simulatorOpen ? 'with-simulator' : ''}`}>
                <div className="editor-container">
                  <DecisionGraph
                    value={currentRule}
                    onChange={(val) => setCurrentRule(val as any)}
                    defaultActivePanel="components"
                    components={customComponents}
                  />
                </div>
                
                {simulatorOpen && (
                  <div className="integrated-simulator">
                    <div className="simulator-content">
                      <div className="simulator-left-panel">
                        <div className="default-request-panel">
                          <h4>Default request</h4>
                          <textarea
                            value={simData}
                            onChange={(e) => setSimData(e.target.value)}
                            className="simulator-json-input"
                            rows={15}
                            placeholder='{"creditScore": 750, "annualIncome": 72000, "monthlyDebt": 1800, "monthlyIncome": 6000}'
                          />
                          <button 
                            className="btn-primary simulate-btn"
                            onClick={simulateRule}
                            disabled={loading}
                          >
                            {loading ? 'Simulating...' : '▶ Simulate'}
                          </button>
                        </div>
                      </div>
                      
                      <div className="simulator-middle-panel">
                        {ruleSimResult && ruleSimResult.success === false && (
                          <div className="simulation-error">
                            <h4>❌ Simulation Error</h4>
                            <div className="error-details">
                              <p><strong>Error:</strong> {ruleSimResult.error || 'Unknown error'}</p>
                              {ruleSimResult.details && (
                                <p><strong>Details:</strong> {ruleSimResult.details}</p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {ruleSimResult && ruleSimResult.success !== false && ruleSimResult.components && (
                          <div className="node-execution-trace">
                            <h4>Execution trace</h4>
                            <div className="trace-list">
                              {ruleSimResult.components.map((component: any, index: number) => (
                                <div key={component.id} className="trace-item">
                                  <div className="trace-icon">
                                    {ruleSimResult.success ? '✅' : '❌'}
                                  </div>
                                  <div className="trace-content">
                                    <div className="trace-name">{component.name || component.type}</div>
                                    <div className="trace-time">
                                      {((index + 1) * Math.random() * 50).toFixed(1)}μs
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {!ruleSimResult && (
                          <div className="no-execution">
                            <p>Run simulation to see execution trace</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="simulator-right-panel">
                        <div className="simulator-tabs">
                          <button 
                            className={activeSimTab === 'output' ? 'active' : ''}
                            onClick={() => setActiveSimTab('output')}
                          >
                            Output
                          </button>
                          <button 
                            className={activeSimTab === 'input' ? 'active' : ''}
                            onClick={() => setActiveSimTab('input')}
                          >
                            Input
                          </button>
                          <button 
                            className={activeSimTab === 'trace' ? 'active' : ''}
                            onClick={() => setActiveSimTab('trace')}
                          >
                            Trace
                          </button>
                        </div>
                        
                        <div className="simulator-tab-content">
                          {activeSimTab === 'output' && (
                            <div className="output-content">
                              {ruleSimResult ? (
                                <pre className="json-output">
                                  {JSON.stringify(ruleSimResult.result, null, 2)}
                                </pre>
                              ) : (
                                <div className="empty-state">No output yet</div>
                              )}
                            </div>
                          )}
                          
                          {activeSimTab === 'input' && (
                            <div className="input-content">
                              <pre className="json-input">
                                {JSON.stringify(JSON.parse(simData || '{}'), null, 2)}
                              </pre>
                            </div>
                          )}
                          
                          {activeSimTab === 'trace' && (
                            <div className="trace-content">
                              {ruleSimResult && ruleSimResult.trace ? (
                                <div className="detailed-trace">
                                  <h4 className="trace-title">Execution Log</h4>
                                  {Object.entries(ruleSimResult.trace).map(([nodeId, nodeTrace]: [string, any], index: number) => {
                                    const component = ruleSimResult.components?.find((c: any) => c.id === nodeId);
                                    return (
                                      <div key={nodeId} className="trace-detail-log">
                                        <div className="trace-header-log">
                                          <span className="trace-number">{index + 1}</span>
                                          <span className="trace-name">{component?.name || nodeId}</span>
                                          <span className="trace-type">({component?.type || 'unknown'})</span>
                                        </div>
                                        <div className="trace-data">
                                          <div className="trace-section">
                                            <strong className="trace-label">Input:</strong>
                                            <pre className="trace-json">
                                              {JSON.stringify(nodeTrace.input || nodeTrace, null, 2)}
                                            </pre>
                                          </div>
                                          {nodeTrace.output && (
                                            <div className="trace-section">
                                              <strong className="trace-label">Output:</strong>
                                              <pre className="trace-json">
                                                {JSON.stringify(nodeTrace.output, null, 2)}
                                              </pre>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : ruleSimResult && ruleSimResult.components ? (
                                <div className="detailed-trace">
                                  <p className="trace-info">ℹ️ Basic trace available. Detailed input/output logs not provided by engine.</p>
                                  {ruleSimResult.components.map((component: any, index: number) => (
                                    <div key={component.id} className="trace-detail">
                                      <div className="trace-header">
                                        <span className="trace-number">{index + 1}</span>
                                        <span className="trace-name">{component.name}</span>
                                        <span className="trace-type">({component.type})</span>
                                      </div>
                                      <div className="trace-meta">
                                        ID: {component.id}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="empty-state">Run simulation to see trace details</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'constants' && (
            <div className="constants-editor">
              <div className="constants-header">
                <h2>Lending Constants Configuration</h2>
                <p className="constants-description">
                  Configure credit thresholds, lending limits, interest rates, and lender-specific settings.
                  Changes here affect all rules that reference <code>_constants</code>.
                </p>
                <button
                  className="btn-primary"
                  onClick={saveConstants}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Constants'}
                </button>
              </div>
              {loading && !constantsText ? (
                <div className="loading">Loading constants...</div>
              ) : (
                <textarea
                  className="constants-json-input"
                  value={constantsText}
                  onChange={(e) => setConstantsText(e.target.value)}
                  rows={35}
                  placeholder="Loading..."
                  spellCheck={false}
                />
              )}
            </div>
          )}

          {activeTab === 'users' && isSuperAdmin() && (
            <UserManagement currentUser={{ id: user.id, role: user.role }} />
          )}

          {activeTab === 'domains' && isSuperAdmin() && (
            <DomainManagement />
          )}

          {activeTab === 'apikeys' && (
            <ApiKeys isSuperAdmin={isSuperAdmin()} />
          )}

          {activeTab === 'dashboard' && (
            <Dashboard />
          )}

          {/* Rule Domain Assignment Modal */}
          {showRuleDomainModal && ruleDomainTarget && (
            <div className="modal-overlay">
              <div className="modal">
                <h3>Assign Domains to Rule</h3>
                <p className="modal-subtitle">
                  <strong>{ruleDomainTarget.name}</strong>
                </p>
                <div className="domain-selection">
                  {allDomains.length === 0 ? (
                    <p className="empty-state">No domains available</p>
                  ) : (
                    allDomains.map(domain => (
                      <label key={domain.id} className="domain-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedDomainIds.includes(domain.id)}
                          onChange={() => toggleDomainSelection(domain.id)}
                        />
                        <span
                          className="domain-checkbox-color"
                          style={{ backgroundColor: domain.color }}
                        />
                        <span className="domain-checkbox-name">{domain.name}</span>
                        {domain.description && (
                          <span className="domain-checkbox-desc">{domain.description}</span>
                        )}
                      </label>
                    ))
                  )}
                </div>
                <div className="modal-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => { setShowRuleDomainModal(false); setRuleDomainTarget(null); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={saveRuleDomains}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Version History Modal */}
          {versionHistoryRule && (
            <VersionHistory
              ruleId={versionHistoryRule.id}
              ruleName={versionHistoryRule.name}
              onRollback={() => {
                loadRules();
                setVersionHistoryRule(null);
              }}
              onClose={() => setVersionHistoryRule(null)}
            />
          )}

        </main>
      </div>
    </JdmConfigProvider>
  );
}

export default App;
